import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, RefreshCw, Edit, Eye, EyeOff, Download,
    FileDown, Image as ImageIcon, Trash2, Plus, Link as LinkIcon,
    ArrowLeft, Calendar, Users, Ticket, Settings, Archive, CheckSquare, Square,
    Lock, Mail, X, Upload
} from 'lucide-react';
import ParticipantCard from '../../Component/v2/participant/ParticipantCard';
import EmbedFormModal from '../../Component/v2/participant/EmbedFormModal';
import EditAllCardsModal from '../../Component/v2/participant/EditAllCardsModal';
import DesignCustomizationModal from '../../Component/v2/idcard/DesignCustomizationModal';
import ManageCategoriesModal from '../../Component/v2/event/ManageCategoriesModal';
import ManageVolunteersModal from '../../Component/v2/event/ManageVolunteersModal';
import BulkAddAmenitiesModal from '../../Component/v2/participant/BulkAddAmenitiesModal';
import AmenitiesCheckInModal from '../../Component/v2/participant/AmenitiesCheckInModal';

// ── Background Settings Modal ──────────────────────────────────────────────────
const BackgroundModal = ({ event, onClose, onSaved }) => {
    const [previewUrl, setPreviewUrl] = useState(event?.defaultIdCardBackground || '');
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(''); setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-image`, {
                method: 'POST', body: formData
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Upload failed');
            setPreviewUrl(data.url);
        } catch (err) { setError(err.message || 'Upload failed'); }
        finally { setUploading(false); }
    };

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ defaultIdCardBackground: previewUrl || null })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Save failed');
            onSaved(previewUrl || null);
            onClose();
        } catch (err) { setError(err.message || 'Failed to save'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-lg font-bold text-gray-900">Card Background Image</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="relative w-full h-48 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                        {previewUrl ? (
                            <>
                                <img src={previewUrl} alt="Background preview" className="w-full h-full object-cover" />
                                <button onClick={() => setPreviewUrl('')}
                                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700 shadow">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </>
                        ) : (
                            <div className="text-center text-gray-400">
                                <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                <p className="text-sm">No background set</p>
                                <p className="text-xs mt-0.5">All cards will show a gradient</p>
                            </div>
                        )}
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-indigo-300 text-indigo-700 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 font-medium text-sm disabled:opacity-50 transition-colors">
                        <Upload className="w-4 h-4" />
                        {uploading ? 'Uploading…' : 'Upload Background Image'}
                    </button>
                    {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                    <p className="text-xs text-gray-400">
                        This background applies to <strong>all participant ID cards</strong>. Per-category backgrounds (set in Manage Categories) take priority.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save Background'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const STORAGE_KEY = 'kapconIdCardsAuth';
const EVENT_ID = 'kapcon-conference';

// ── Login Screen ───────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
    const [form, setForm] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPw, setShowPw] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                const auth = { username: form.username, password: form.password, role: data.role };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
                onLogin(auth);
            } else setError(data.message || 'Invalid credentials');
        } catch { setError('Cannot connect to server'); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-gray-900">KAPCON 2026</h1>
                    <p className="text-sm text-gray-500 mt-1">ID Cards Admin</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Username</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type="text" required value={form.username}
                                    onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type={showPw ? 'text' : 'password'} required value={form.password}
                                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                    className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                <button type="button" onClick={() => setShowPw(v => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                        <button type="submit" disabled={loading}
                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-lg disabled:opacity-50 transition-colors">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const ConferenceIdCards = () => {
    const navigate = useNavigate();
    const eventId = EVENT_ID;

    const [auth, setAuth] = useState(null);
    const [event, setEvent] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCards, setShowCards] = useState(true);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showEditAllModal, setShowEditAllModal] = useState(false);
    const [showDesignModal, setShowDesignModal] = useState(false);
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [showVolunteersModal, setShowVolunteersModal] = useState(false);
    const [showBulkAmenitiesModal, setShowBulkAmenitiesModal] = useState(false);
    const [showAmenitiesCheckInModal, setShowAmenitiesCheckInModal] = useState(false);
    const [showBgModal, setShowBgModal] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDownloading, setBulkDownloading] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(0);
    const imgDataCache = useRef({});

    const [elementStyles, setElementStyles] = useState({});
    const [globalVisibility, setGlobalVisibility] = useState({});

    // Load auth from storage
    useEffect(() => {
        try {
            const s = localStorage.getItem(STORAGE_KEY);
            if (s) setAuth(JSON.parse(s));
        } catch {}
    }, []);

    useEffect(() => {
        if (auth) fetchEventAndParticipants();
    }, [auth, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (event) fetchDesignSettings();
    }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchEventAndParticipants = async () => {
        setLoading(true);
        try {
            const [eventRes, participantRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}`),
                fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants?archived=${showArchived}`)
            ]);
            const eventData = await eventRes.json();
            const participantData = await participantRes.json();
            if (eventData.success) setEvent(eventData.data);
            if (participantData.success) setParticipants(participantData.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDesignSettings = async () => {
        if (!event) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/design`);
            const data = await res.json();
            if (data.success && data.data) {
                const dynamicDefaults = getDefaultElementStyles(event);
                setElementStyles({ ...dynamicDefaults, ...data.data.elementStyles });
                setGlobalVisibility({ ...getDefaultVisibility(event), ...data.data.visibility });
            } else {
                setElementStyles(getDefaultElementStyles(event));
                setGlobalVisibility(getDefaultVisibility(event));
            }
        } catch {
            setElementStyles(getDefaultElementStyles(event));
            setGlobalVisibility(getDefaultVisibility(event));
        }
    };

    const handleCategoriesSaved = (updatedCategories) => {
        setEvent(prev => ({ ...prev, participantCategories: updatedCategories }));
    };

    const handleBgSaved = (bgUrl) => {
        setEvent(prev => ({ ...prev, defaultIdCardBackground: bgUrl }));
        imgDataCache.current = {};
    };

    const handleSaveDesignSettings = async (newElementStyles, newVisibility) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/design`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ elementStyles: newElementStyles, visibility: newVisibility })
            });
            const data = await res.json();
            if (data.success) {
                setElementStyles(newElementStyles);
                setGlobalVisibility(newVisibility);
                alert('Design settings saved!');
            }
        } catch { alert('Failed to save design settings'); }
    };

    const handleDeleteParticipant = async (participantId) => {
        if (!window.confirm('Are you sure you want to delete this participant?')) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) setParticipants(prev => prev.filter(p => p._id !== participantId));
        } catch (err) { console.error('Error deleting participant:', err); }
    };

    const handleArchiveParticipant = async (participantId) => {
        if (!window.confirm('Archive this participant?')) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}/archive`, { method: 'PUT' });
            const data = await res.json();
            if (data.success) setParticipants(prev => prev.filter(p => p._id !== participantId));
        } catch (err) { console.error('Error archiving participant:', err); }
    };

    const handleRestoreParticipant = async (participantId) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/${participantId}/restore`, { method: 'PUT' });
            const data = await res.json();
            if (data.success) setParticipants(prev => prev.filter(p => p._id !== participantId));
        } catch (err) { console.error('Error restoring participant:', err); }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => setSelectedIds(new Set(filteredParticipants.map(p => p._id)));
    const deselectAll = () => setSelectedIds(new Set());
    const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Permanently delete ${selectedIds.size} participant(s)?`)) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants/bulk-delete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: [...selectedIds] })
            });
            const data = await res.json();
            if (data.success) {
                setParticipants(prev => prev.filter(p => !selectedIds.has(p._id)));
                setSelectedIds(new Set());
                setSelectionMode(false);
            }
        } catch (err) { console.error('Bulk delete failed:', err); }
    };

    // ── Canvas card renderer ───────────────────────────────────────────────────
    const CARD_W = 430, CARD_H = 610;

    const fetchAsDataUrl = async (url) => {
        if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;
        if (imgDataCache.current[url]) return imgDataCache.current[url];
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/proxy-image?url=${encodeURIComponent(url)}`);
            if (!res.ok) return null;
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            imgDataCache.current[url] = dataUrl;
            return dataUrl;
        } catch { return null; }
    };

    const prefetchAllImages = async () => {
        const urls = new Set();
        if (event.defaultIdCardBackground) urls.add(event.defaultIdCardBackground);
        event.participantCategories?.forEach(c => { if (c.idCardBackground) urls.add(c.idCardBackground); });
        const fileFieldIds = (event.participantFields || []).filter(f => f.fieldType === 'file').map(f => f.id);
        participants.forEach(p => { fileFieldIds.forEach(id => { const v = p.data?.[id]; if (v) urls.add(v); }); });
        await Promise.all([...urls].map(fetchAsDataUrl));
    };

    const loadImg = (src) => new Promise(resolve => {
        if (!src) return resolve(null);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });

    const resolvePos = (style, elemH) => {
        const x = style.left === '50%' ? CARD_W / 2 : style.left === '100%' ? CARD_W : typeof style.left === 'number' ? style.left : CARD_W / 2;
        const y = style.top !== undefined ? style.top : CARD_H - (style.bottom || 0) - elemH;
        return { x, y };
    };

    const getParticipantBg = (participant) => {
        if (participant.categoryId && event.participantCategories) {
            const cat = event.participantCategories.find(c => c.id === participant.categoryId);
            if (cat?.idCardBackground) return cat.idCardBackground;
        }
        return event.defaultIdCardBackground || null;
    };

    const roundRect = (ctx, x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
    };

    const generateCardCanvas = async (participant) => {
        const SCALE = 2;
        const canvas = document.createElement('canvas');
        canvas.width = CARD_W * SCALE; canvas.height = CARD_H * SCALE;
        const ctx = canvas.getContext('2d');
        ctx.scale(SCALE, SCALE);

        const bgUrl = getParticipantBg(participant);
        const bgDataUrl = bgUrl ? imgDataCache.current[bgUrl] : null;
        if (bgDataUrl) {
            const bgImg = await loadImg(bgDataUrl);
            if (bgImg) ctx.drawImage(bgImg, 0, 0, CARD_W, CARD_H);
        } else {
            const grad = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
            grad.addColorStop(0, '#667eea'); grad.addColorStop(1, '#764ba2');
            ctx.fillStyle = grad; ctx.fillRect(0, 0, CARD_W, CARD_H);
        }

        for (const field of (event.participantFields || [])) {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            const style = elementStyles?.[fieldKey];
            const visible = globalVisibility?.[fieldKey];
            const value = participant.data?.[field.id];
            if (!visible || !value || !style) continue;

            if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                const size = style.size || 170;
                const { x, y } = resolvePos(style, size);
                const px = x - size / 2;
                const photoDataUrl = imgDataCache.current[value];
                if (photoDataUrl) {
                    const photo = await loadImg(photoDataUrl);
                    if (photo) {
                        ctx.save();
                        ctx.beginPath(); ctx.arc(px + size / 2, y + size / 2, size / 2 + 4, 0, Math.PI * 2);
                        ctx.fillStyle = 'white'; ctx.fill();
                        ctx.beginPath(); ctx.arc(px + size / 2, y + size / 2, size / 2, 0, Math.PI * 2); ctx.clip();
                        ctx.drawImage(photo, px, y, size, size); ctx.restore();
                    }
                }
            } else {
                const fontSize = style.fontSize || 16;
                const isOrg = field.fieldName.toLowerCase().includes('organization');
                const { x, y } = resolvePos(style, fontSize);
                ctx.font = `${field.fieldName.toLowerCase().includes('name') ? 'bold' : 'normal'} ${fontSize}px Arial, sans-serif`;
                ctx.fillStyle = style.color || 'black';
                ctx.textAlign = style.textAlign || 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(isOrg ? String(value).toUpperCase() : String(value), x, y, CARD_W * 0.9);
            }
        }

        if (globalVisibility?.qrCode && elementStyles?.qrCode) {
            const qrSt = elementStyles.qrCode;
            const qrSize = qrSt.size || 100;
            const qrX = qrSt.left === '50%' ? CARD_W / 2 : (typeof qrSt.left === 'number' ? qrSt.left : CARD_W / 2);
            const qrY = qrSt.top !== undefined ? qrSt.top : CARD_H - (qrSt.bottom || 0) - qrSize - 36;
            try {
                const QRLib = (await import('qrcode')).default;
                const qrDataUrl = await QRLib.toDataURL(`${window.location.origin}/v2/checkin/${participant._id}`, { width: qrSize * SCALE, margin: 1, errorCorrectionLevel: 'H' });
                const qrImg = await loadImg(qrDataUrl);
                if (qrImg) {
                    const PAD = 8, LABEL_H = 22;
                    const boxW = qrSize + PAD * 2, boxH = LABEL_H + qrSize + PAD;
                    const boxX = qrX - boxW / 2, boxY = qrY - LABEL_H;
                    ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.15)'; ctx.shadowBlur = 6;
                    ctx.fillStyle = 'white'; roundRect(ctx, boxX, boxY, boxW, boxH, 8); ctx.fill(); ctx.restore();
                    ctx.font = 'bold 11px Arial, sans-serif'; ctx.fillStyle = 'black'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
                    ctx.fillText('Show at the Venue', qrX, boxY + LABEL_H / 2);
                    ctx.drawImage(qrImg, qrX - qrSize / 2, qrY, qrSize, qrSize);
                }
            } catch {}
        }

        if (globalVisibility?.participantId && elementStyles?.participantId && participant.registrationId) {
            const idSt = elementStyles.participantId;
            const fontSize = idSt.fontSize || 12;
            const idX = idSt.left === '50%' ? CARD_W / 2 : (typeof idSt.left === 'number' ? idSt.left : CARD_W / 2);
            const idY = idSt.top !== undefined ? idSt.top : CARD_H - (idSt.bottom || 0) - fontSize - 2;
            ctx.font = `${fontSize}px Arial, sans-serif`; ctx.fillStyle = idSt.color || 'black';
            ctx.textAlign = idSt.textAlign || 'center'; ctx.textBaseline = 'top';
            ctx.fillText(`ID: ${participant.registrationId}`, idX, idY);
        }

        return canvas.toDataURL('image/png');
    };

    const handleDownloadAllPNG = async () => {
        if (participants.length === 0) return;
        setBulkDownloading('png'); setBulkProgress(0); imgDataCache.current = {};
        try {
            await prefetchAllImages();
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            const nameField = event.participantFields?.[0]?.id;
            for (let i = 0; i < participants.length; i++) {
                const p = participants[i];
                const dataUrl = await generateCardCanvas(p);
                const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
                const rawName = (nameField && p.data?.[nameField]) || p.registrationId || p._id;
                zip.file(`${String(rawName).replace(/[^a-zA-Z0-9-_]/g, '_')}.png`, base64, { base64: true });
                setBulkProgress(Math.round((i + 1) / participants.length * 100));
            }
            const blob = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'KAPCON2026-cards.zip'; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { alert(`Failed: ${err?.message || err}`); }
        finally { setBulkDownloading(null); setBulkProgress(0); }
    };

    const handleDownloadAllPDF = async () => {
        if (participants.length === 0) return;
        setBulkDownloading('pdf'); setBulkProgress(0); imgDataCache.current = {};
        try {
            await prefetchAllImages();
            const { jsPDF } = await import('jspdf');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [CARD_W, CARD_H] });
            for (let i = 0; i < participants.length; i++) {
                if (i > 0) pdf.addPage([CARD_W, CARD_H]);
                pdf.addImage(await generateCardCanvas(participants[i]), 'PNG', 0, 0, CARD_W, CARD_H);
                setBulkProgress(Math.round((i + 1) / participants.length * 100));
            }
            pdf.save('KAPCON2026-cards.pdf');
        } catch (err) { alert(`Failed: ${err?.message || err}`); }
        finally { setBulkDownloading(null); setBulkProgress(0); }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const filteredParticipants = participants.filter(p =>
        Object.values(p.data || {}).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);
    const paginatedParticipants = filteredParticipants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm, showArchived]);

    // ── Auth gate ──────────────────────────────────────────────────────────────
    if (!auth) return <LoginScreen onLogin={a => setAuth(a)} />;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <button onClick={() => navigate('/event/jss/admin/data')}
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin Data
                    </button>
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">KAPCON 2026 – Conference ID Cards</h1>
                            <div className="flex items-center space-x-4 mt-2">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Calendar className="w-4 h-4 mr-1" /> 23 Jan 2026
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <Users className="w-4 h-4 mr-1" /> {participants.length} Participants
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button onClick={() => setShowEmbedModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700">
                                <LinkIcon className="w-4 h-4 mr-2" /> Registration Links
                            </button>
                            <button onClick={() => { localStorage.removeItem(STORAGE_KEY); setAuth(null); }}
                                className="inline-flex items-center px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm font-medium rounded-lg hover:bg-red-100">
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex-1 min-w-[200px] max-w-md relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search participants..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                        </div>

                        <div className="flex rounded-lg bg-gray-100 p-1">
                            {[['Active', false], ['Archived', true]].map(([label, val]) => (
                                <button key={label} onClick={() => setShowArchived(val)}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${showArchived === val ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center flex-wrap gap-2">
                            <button onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
                                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${selectionMode ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                                {selectionMode ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                                {selectionMode ? `Selecting (${selectedIds.size})` : 'Select'}
                            </button>
                            <button onClick={fetchEventAndParticipants}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                <RefreshCw className="w-4 h-4 mr-2" /> Reload
                            </button>
                            <button onClick={() => setShowEditAllModal(true)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                <Edit className="w-4 h-4 mr-2" /> Edit All Cards
                            </button>
                            <button onClick={() => setShowBgModal(true)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                                <ImageIcon className="w-4 h-4 mr-2" />
                                {event?.defaultIdCardBackground ? 'Change BG' : 'Set BG'}
                                {event?.defaultIdCardBackground && (
                                    <span className="ml-1.5 w-2 h-2 rounded-full bg-green-500 inline-block" title="Background set" />
                                )}
                            </button>
                            <button onClick={() => setShowDesignModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 shadow-md">
                                <Settings className="w-4 h-4 mr-2" /> Customize Design
                            </button>
                            <button onClick={() => setShowCategoriesModal(true)}
                                className="inline-flex items-center px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                                <Users className="w-4 h-4 mr-2" /> Manage Categories
                                {event?.participantCategories?.length > 0 && (
                                    <span className="ml-2 bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">{event.participantCategories.length}</span>
                                )}
                            </button>
                            <button onClick={() => navigate('/event/jss/admin/scan')}
                                className="inline-flex items-center px-3 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900">
                                <span className="mr-2">📷</span> Scan / Check-in
                            </button>
                            <button onClick={() => navigate('/event/jss/admin/conference/dashboard')}
                                className="inline-flex items-center px-3 py-2 bg-indigo-700 text-white text-sm font-medium rounded-lg hover:bg-indigo-800">
                                <span className="mr-2">📊</span> Dashboard
                            </button>
                            <button onClick={() => setShowCards(!showCards)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">
                                {showCards ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                {showCards ? 'Hide' : 'Show'} Cards
                            </button>
                            {(event?.amenities?.length > 0 || event?.participantCategories?.some(c => c.amenities?.length > 0)) && (
                                <>
                                    <button onClick={() => setShowBulkAmenitiesModal(true)}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
                                        <span className="mr-2">🎁</span> Add Amenities
                                    </button>
                                    <button onClick={() => setShowAmenitiesCheckInModal(true)}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700">
                                        <span className="mr-2">📋</span> Amenity Stats
                                    </button>
                                </>
                            )}
                            <button onClick={handleDownloadAllPNG} disabled={bulkDownloading !== null}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                <ImageIcon className="w-4 h-4 mr-2" />
                                {bulkDownloading === 'png' ? `PNG ${bulkProgress}%` : 'Bulk PNG'}
                            </button>
                            <button onClick={handleDownloadAllPDF} disabled={bulkDownloading !== null}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                <FileDown className="w-4 h-4 mr-2" />
                                {bulkDownloading === 'pdf' ? `PDF ${bulkProgress}%` : 'Bulk PDF'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Participants Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {filteredParticipants.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No participants found</h3>
                        <p className="text-gray-600">{searchTerm ? `No results for "${searchTerm}"` : 'No participants in this view.'}</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {paginatedParticipants.map((participant, index) => (
                                <ParticipantCard
                                    key={participant._id}
                                    participant={participant}
                                    event={event}
                                    showCard={showCards}
                                    onDelete={() => handleDeleteParticipant(participant._id)}
                                    onRefresh={fetchEventAndParticipants}
                                    elementStyles={elementStyles}
                                    globalVisibility={globalVisibility}
                                    index={index}
                                    onArchive={handleArchiveParticipant}
                                    onRestore={handleRestoreParticipant}
                                    selectionMode={selectionMode}
                                    selected={selectedIds.has(participant._id)}
                                    onSelect={() => toggleSelect(participant._id)}
                                />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-8 rounded-lg shadow-sm">
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>–<span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredParticipants.length)}</span> of <span className="font-medium">{filteredParticipants.length}</span>
                                    </p>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" /></svg>
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                            <button key={p} onClick={() => handlePageChange(p)}
                                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === p ? 'z-10 bg-indigo-600 text-white' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'}`}>{p}</button>
                                        ))}
                                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Bulk selection bar */}
            {selectionMode && (
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-semibold text-gray-800">{selectedIds.size} selected</span>
                            <button onClick={selectAll} className="text-sm text-blue-600 hover:underline font-medium">Select all {filteredParticipants.length}</button>
                            {selectedIds.size > 0 && <button onClick={deselectAll} className="text-sm text-gray-500 hover:underline">Deselect all</button>}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={exitSelectionMode} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                            <button onClick={handleBulkDelete} disabled={selectedIds.size === 0}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                <Trash2 className="w-4 h-4 mr-1.5" /> Delete {selectedIds.size > 0 ? selectedIds.size : ''} Selected
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showBgModal && event && <BackgroundModal event={event} onClose={() => setShowBgModal(false)} onSaved={handleBgSaved} />}
            {showEmbedModal && <EmbedFormModal event={event} onClose={() => setShowEmbedModal(false)} />}
            {showEditAllModal && <EditAllCardsModal event={event} onClose={() => setShowEditAllModal(false)} onSave={fetchEventAndParticipants} />}
            {showDesignModal && (
                <DesignCustomizationModal event={event} onClose={() => setShowDesignModal(false)}
                    onSave={handleSaveDesignSettings} initialStyles={elementStyles} initialVisibility={globalVisibility} />
            )}
            {showCategoriesModal && event && (
                <ManageCategoriesModal event={event} onClose={() => setShowCategoriesModal(false)} onSaved={handleCategoriesSaved} />
            )}
            {showBulkAmenitiesModal && event && (
                <BulkAddAmenitiesModal event={event} onClose={() => setShowBulkAmenitiesModal(false)} onDone={fetchEventAndParticipants} />
            )}
            {showAmenitiesCheckInModal && event && (
                <AmenitiesCheckInModal event={event} participants={participants} onClose={() => setShowAmenitiesCheckInModal(false)} />
            )}
        </div>
    );
};

// ── Default style helpers ──────────────────────────────────────────────────────
function getDefaultElementStyles(event) {
    const styles = {};
    let currentTop = 163;
    if (event?.participantFields) {
        event.participantFields.forEach(field => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                styles[fieldKey] = { bottom: 160, left: '50%', size: 170 };
            } else {
                const fontSize = field.fieldName.toLowerCase().includes('name') ? 29 :
                    field.fieldName.toLowerCase().includes('email') ? 14 : 16;
                styles[fieldKey] = { top: currentTop, left: '50%', fontSize, color: 'black', textAlign: 'center' };
                currentTop += fontSize + 10;
            }
        });
    }
    styles.qrCode = { bottom: 215, left: '50%', size: 100 };
    styles.participantId = { bottom: 3, left: '50%', fontSize: 12, color: 'black', textAlign: 'center' };
    return styles;
}

function getDefaultVisibility(event) {
    const visibility = {};
    if (event?.participantFields) {
        event.participantFields.forEach(field => {
            visibility[field.fieldName.toLowerCase().replace(/\s+/g, '')] = true;
        });
    }
    visibility.qrCode = true;
    visibility.participantId = true;
    visibility.isLandscapePrint = false;
    return visibility;
}

export default ConferenceIdCards;
